import React, { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAgentConversations } from '../../../contexts/AgentConversationsContext';
import type { Conversation, ConversationListFilter } from '../../../models/agentConversation';
import ConversationRow from './ConversationRow';
import { groupByDate, groupByPatient } from './conversationPresentation';
import { BetaBadge, CloseButton, ConnectionDot } from './PanelChrome';
import { useConversationActions } from './useConversationActions';
import { useOpenPatientPage } from './useOpenPatientPage';

const FILTERS: { value: ConversationListFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'general', label: 'Generales' },
  { value: 'archived', label: 'Archivadas' },
];

const SEARCH_DEBOUNCE_MS = 300;

// One way to group at a time, remembered per browser: by date (each row
// shows its patient) or by patient (general conversations last).
type Grouping = 'date' | 'patient';
const GROUPING_KEY = 'clinic-agent-list-grouping';
const GROUPINGS: { value: Grouping; label: string }[] = [
  { value: 'date', label: 'Fecha' },
  { value: 'patient', label: 'Paciente' },
];

function readGrouping(): Grouping {
  try {
    return localStorage.getItem(GROUPING_KEY) === 'patient' ? 'patient' : 'date';
  } catch {
    return 'date';
  }
}

const EMPTY_TEXT: Record<ConversationListFilter, { title: string; body: string }> = {
  all: {
    title: 'Aún no tienes conversaciones',
    body: 'Empieza una desde aquí o desde la ficha de un paciente.',
  },
  general: {
    title: 'No tienes conversaciones generales',
    body: 'Las conversaciones sin paciente aparecen aquí.',
  },
  archived: {
    title: 'No tienes conversaciones archivadas',
    body: 'Archiva una conversación desde su menú para sacarla de la lista sin borrarla.',
  },
};

// `panel`: docked on the right of a desktop (see CloseButton).
const ConversationList: React.FC<{ onClose?: () => void; panel?: boolean }> = ({ onClose, panel = false }) => {
  const agent = useAgentConversations()!;
  const actions = useConversationActions();
  const openPatientPage = useOpenPatientPage();
  const [query, setQuery] = useState(agent.listQuery);
  const [grouping, setGroupingState] = useState<Grouping>(readGrouping);
  const setGrouping = (g: Grouping) => {
    setGroupingState(g);
    try {
      localStorage.setItem(GROUPING_KEY, g);
    } catch {
      /* not remembered this time */
    }
  };
  const { setListQuery } = agent;

  useEffect(() => {
    const handle = setTimeout(() => setListQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, setListQuery]);

  const activeId = agent.active?.kind === 'conversation' ? agent.active.id : null;
  const searching = agent.listQuery.trim() !== '';

  // Each conversation once, in place: the open one is highlighted, not moved.
  const groups = grouping === 'patient' ? groupByPatient(agent.conversations) : groupByDate(agent.conversations);

  const newDraft = () =>
    agent.startDraft(agent.routePatient?.name ? agent.routePatient : null);

  const row = (c: Conversation, showPatient: boolean) => (
    <li key={c.id}>
      <ConversationRow
        conversation={c}
        current={c.id === activeId}
        unread={agent.unread.has(c.id)}
        showPatient={showPatient}
        onOpen={() => {
          agent.openConversation(c.id);
          openPatientPage(c.patient?.id);
        }}
        onRename={(title) => actions.rename(c, title)}
        onToggleArchive={() => actions.toggleArchive(c)}
        onDelete={() => actions.remove(c)}
      />
    </li>
  );

  const nothingLoaded = agent.conversations.length === 0;

  return (
    <>
      {/* Same height as the page header (HeaderBar: 64px + its border). */}
      <div className="box-border flex min-h-[65px] items-center gap-1.5 border-b border-border pr-2.5 pl-3.5">
        <ConnectionDot state={agent.connectionState} />
        <h2 className="m-0 flex flex-1 items-center gap-1.5 text-[15px] font-bold">
          Sissi AI <BetaBadge />
        </h2>
        <Button variant="ghost" size="icon-sm" onClick={newDraft} aria-label="Nueva conversación" title="Nueva conversación">
          <Plus />
        </Button>
        {onClose && <CloseButton onClose={onClose} panel={panel} />}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="sticky top-0 z-[1] grid gap-2 border-b border-border bg-card px-3 pt-3 pb-2">
          <label className="flex h-(--control-height) items-center gap-2 rounded-control border border-input bg-card px-3 text-muted-foreground focus-within:outline-2 focus-within:outline-ring">
            <Search className="size-4 shrink-0" aria-hidden="true" />
            <span className="sr-only">Buscar conversaciones por título</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por título…"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground outline-none"
            />
          </label>
          <div role="group" aria-label="Filtrar conversaciones" className="flex rounded-[9px] border border-border bg-muted p-[3px]">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={agent.listFilter === f.value}
                onClick={() => agent.setListFilter(f.value)}
                className={cn(
                  'h-7 flex-1 cursor-pointer rounded-[7px] border-0 bg-transparent text-[13px] font-medium text-muted-foreground',
                  agent.listFilter === f.value && 'bg-card font-semibold text-foreground shadow-xs',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span id="grouping-label">Agrupar por</span>
            <div role="group" aria-labelledby="grouping-label" className="flex gap-0.5">
              {GROUPINGS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  aria-pressed={grouping === g.value}
                  onClick={() => setGrouping(g.value)}
                  className={cn(
                    'box-border h-6 cursor-pointer rounded-md border-0 bg-transparent px-2 text-xs font-medium text-muted-foreground hover:bg-muted',
                    grouping === g.value && 'bg-primary-subtle font-semibold text-primary hover:bg-primary-subtle',
                  )}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {agent.listError && nothingLoaded ? (
          <div role="alert" className="px-6 py-9 text-center text-[13.5px] text-muted-foreground">
            <p className="m-0 mb-3">No se pudieron cargar las conversaciones.</p>
            <Button variant="outline" size="sm" onClick={agent.reloadConversations}>
              Reintentar
            </Button>
          </div>
        ) : agent.listLoading && nothingLoaded ? (
          <p className="m-0 px-6 py-9 text-center text-[13.5px] text-muted-foreground">Cargando conversaciones…</p>
        ) : groups.length === 0 ? (
          <div className="px-6 py-9 text-center text-[13.5px] text-muted-foreground">
            <p className="m-0 mb-1.5 text-[15px] font-semibold text-foreground">
              {searching ? 'Ninguna conversación tiene ese título' : EMPTY_TEXT[agent.listFilter].title}
            </p>
            <p className="m-0">{searching ? 'Prueba con otra palabra del título.' : EMPTY_TEXT[agent.listFilter].body}</p>
            {!searching && agent.listFilter === 'all' && (
              <Button size="sm" className="mt-4" onClick={newDraft}>
                <Plus /> Nueva conversación
              </Button>
            )}
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-label={g.label}>
              <h3 className="m-0 px-3.5 pt-3.5 pb-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                {g.label}
              </h3>
              {/* Grouped by patient, the heading already names the patient. */}
              <ul className="m-0 list-none p-0">{g.conversations.map((c) => row(c, grouping === 'date'))}</ul>
            </section>
          ))
        )}

        {agent.hasMoreConversations && !nothingLoaded && (
          <div className="p-3 text-center">
            <Button variant="outline" size="sm" onClick={agent.loadMoreConversations} disabled={agent.listLoading}>
              {agent.listLoading ? 'Cargando…' : 'Cargar más'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default ConversationList;
